import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  AuthSlice,
  AuthUser,
  AuthTokens,
  LoginPayload,
  AuthStatus,
} from '@/features/auth/types/auth.types';

// ─── Auth Store ─────────────────────────────────────────────────────────
// Security: Refresh tokens are stored in HttpOnly cookies (not in JS state).
// Access tokens are stored in memory (not persisted to localStorage).
// Only user metadata and session state survive page refresh.

export const useAuthStore = create<AuthSlice>()(
  persist(
    (set) => ({
      // State
      user: null,
      accessToken: null,
      status: 'idle' as AuthStatus,
      sessionId: null,
      rememberMe: false,

      // Actions
      setAuth: (payload: LoginPayload, rememberMe: boolean) =>
        set({
          user: payload.user,
          accessToken: payload.tokens.access.accessToken,
          status: 'authenticated',
          sessionId: payload.sessionId,
          rememberMe,
        }),

      setUser: (user: AuthUser) =>
        set({ user }),

      setTokens: (tokens: AuthTokens) =>
        set({
          accessToken: tokens.access.accessToken,
        }),

      clearSession: () =>
        set({
          user: null,
          accessToken: null,
          status: 'unauthenticated',
          sessionId: null,
          rememberMe: false,
        }),

      setStatus: (status: AuthStatus) =>
        set({ status }),
    }),
    {
      name: 'auth-store',
      // Use sessionStorage for slightly better security than localStorage
      // Only user metadata is persisted — access tokens are ephemeral
      storage: createJSONStorage(() => sessionStorage),
      // ⚠️  Only persist non-security-sensitive hints.
      // DO NOT persist `status` or `user` — auth state must always be
      // determined by server-side validation (GET /auth/me) on every
      // page load. Persisting `status: 'authenticated'` creates a race
      // condition where the validation query never fires, and a stale
      // 'authenticated' status can persist indefinitely in sessionStorage.
      partialize: (state) => ({
        sessionId: state.sessionId,
        rememberMe: state.rememberMe,
      }),
    },
  ),
);
